import { API_ENDPOINTS, withParams } from '../config/apis';
import { apiRequest } from '../api/client';
import type { EventType } from '../components/event/eventType';

/** 목록 항목 — 백엔드 EventResponse와 동일 필드 */
export type EventSummary = {
  id: number;
  eventCode: string;
  title: string;
  city: string;
  location: string;
  hostName: string;
  eventDate: string; // 'YYYY-MM-DD'
  type: EventType;
  registrationStartDate: string | null; // 'YYYY-MM-DD'
  registrationEndDate: string | null; // 'YYYY-MM-DD'
  registerUrl: string;
  isPublished: boolean;
  isEnd: boolean;
  imageUrl: string;
  collectBirth: boolean; // 참가자 생년월일 수집(=필수) 여부
  collectGender: boolean; // 참가자 성별 수집(=필수) 여부
};

/** 수정 요청 본문 — 백엔드 EventUpdateRequest (eventCode·imageUrl 제외) */
export type EventUpdateRequest = {
  title?: string;
  city?: string;
  location?: string;
  hostName?: string;
  eventDate?: string;
  type?: EventType;
  registrationStartDate?: string;
  registrationEndDate?: string;
  registerUrl?: string;
  isPublished?: boolean;
  isEnd?: boolean;
  collectBirth?: boolean;
  collectGender?: boolean;
};

/** 생성 요청 본문 — 백엔드 EventCreateRequest */
export type EventCreateRequest = {
  title: string;
  city: string;
  location: string;
  hostName: string;
  eventDate: string;
  type: EventType;
  registrationStartDate?: string;
  registrationEndDate?: string;
  registerUrl?: string;
  isPublished: boolean;
  collectBirth?: boolean;
  collectGender?: boolean;
};

// 백엔드는 생성/수정을 multipart 로 받음: 'event'(JSON 파트) + 'image'(파일 파트)
function toEventFormData(payload: object, image?: File | null) {
  const form = new FormData();
  // @RequestPart('event') 가 JSON 으로 역직렬화되도록 Blob 의 type 을 명시
  form.append('event', new Blob([JSON.stringify(payload)], { type: 'application/json' }));
  if (image) {
    form.append('image', image);
  }
  return form;
}

export const eventService = {
  list: () =>
    apiRequest<{ events: EventSummary[] }>(API_ENDPOINTS.eventList)
      .then((res) => res.events),

  detail: (eventId: number) =>
    apiRequest<EventSummary>(withParams(API_ENDPOINTS.eventDetail, { eventId })),

  create: (body: EventCreateRequest, image?: File | null) =>
    apiRequest<EventSummary, FormData>(API_ENDPOINTS.eventCreate, {
      body: toEventFormData(body, image),
    }),

  update: (eventId: number, body: EventUpdateRequest, image?: File | null) =>
    apiRequest<EventSummary, FormData>(
      withParams(API_ENDPOINTS.eventUpdate, { eventId }),
      {
        body: toEventFormData(body, image),
      }
    ),

  delete: (eventId: number) =>
    apiRequest<void>(withParams(API_ENDPOINTS.eventDelete, { eventId })),

  duplicate: (eventId: number) =>
    apiRequest<EventSummary>(withParams(API_ENDPOINTS.eventDuplicate, { eventId })),
};
